import {useState} from 'react';
import {useAudioRecorder} from 'react-use-audio-recorder';
import {useMutation} from '@tanstack/react-query';
import {Download, Mic, Pause, Play, Send, Square, Trash2} from 'lucide-react';
import {UploadUrl} from "@/components/upload";
import axios from "axios";
import {getAuthToken} from "@/lib/services/instance";
import {myAcademySlug} from "@/lib/services/academyService";
import {useApiLmsSaveFileUploaded} from "@/api/lms";
import ApiHandleSuccessMessage, {ApiHandleErrorMessage} from "@/lib/helper/apiHandleResultMessage";

export default function AudioRecorder({lessonDetail}) {
    const {
        recordingStatus,
        recordingTime,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        getBlob,
    } = useAudioRecorder();

    const [hasRecording, setHasRecording] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    const {mutate: saveAudio, isPending} = useApiLmsSaveFileUploaded()

    const uploadMutation = useMutation({
        mutationFn: async (file) => {
            const formData = new FormData();
            formData.append("file", file, `recording-${Date.now()}.wav`);

            const res = await axios.post(UploadUrl(), formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "Authorization": getAuthToken(),
                    'X-Academy': myAcademySlug()
                },
            });

            saveAudio({
                id: lessonDetail?.data?.data?.id,
                file_id: res?.data?.data?.data?.id,

            }, {
                onSuccess: res => {
                    ApiHandleSuccessMessage(res)
                },
                onError: err => {
                    ApiHandleErrorMessage(err)
                }
            })
            return res.data;
        },
        onSuccess: (r) => {

        },
        onError: (err) => {
            alert("مشکل در آپلود ❌");
            console.error(err);
        },
    });


    const togglePauseResume = () => {
        if (recordingStatus === 'recording') {
            pauseRecording();
            setIsPaused(true);
        }

        if (recordingStatus === 'paused') {
            resumeRecording();
            setIsPaused(false);
        }
    };

    const handleStartRecording = () => {
        startRecording();
        setHasRecording(false);
        setIsRecording(true);
        setIsPaused(false);
    };

    const handleStopRecording = () => {
        stopRecording();
        setHasRecording(true);
        setIsRecording(false);
        setIsPaused(false);
    };

    const handleDownload = () => {
        if (recordingStatus === 'stopped') {
            const url = URL.createObjectURL(getBlob());
            const a = document.createElement('a');
            a.href = url;
            a.download = `recording-${Date.now()}.webm`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const handleSend = () => {
        const blob = getBlob();
        if (blob) {
            uploadMutation.mutate(blob);
        }
    };

    const handleDelete = () => {
        setHasRecording(false);
        setIsRecording(false);
        setIsPaused(false);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (<div className="w-full max-w-md  rounded-3xl shadow-2xl p-8 space-y-6">
        <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold  ">ضبط صدا</h1>
            <p className="text-slate-500">صدای خود را ضبط و ارسال کنید</p>
        </div>

        <div className="flex flex-col items-center space-y-6">
            {isRecording && (<div className="relative">
                <div className="absolute inset-0 animate-ping">
                    <div className="w-32 h-32 rounded-full bg-red-400 opacity-75"></div>
                </div>
                <div
                    className="relative flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-red-500 to-red-600 shadow-lg">
                    <div className="w-24 h-24 rounded-full bg-red-400 animate-pulse"></div>
                </div>
            </div>)}

            {!isRecording && !hasRecording && (<div
                className="w-32 h-32 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center shadow-lg">
                <Mic className="w-16 h-16  "/>
            </div>)}

            {!isRecording && hasRecording && (<div
                className="w-32 h-32 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                <Mic className="w-16 h-16 text-white"/>
            </div>)}

            <div className="text-center">
                <div className="text-4xl font-mono font-bold  ">
                    {formatTime(recordingTime)}
                </div>
                <div className="mt-2 text-sm text-slate-500">
                    {isRecording && !isPaused && 'در حال ضبط...'}
                    {isRecording && isPaused && 'متوقف شده'}
                    {!isRecording && hasRecording && 'ضبط تکمیل شد'}
                    {!isRecording && !hasRecording && 'آماده ضبط'}
                </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-center w-full">
                {!isRecording && !hasRecording && (<button
                    onClick={handleStartRecording}
                    className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                    <Mic className="w-5 h-5"/>
                    شروع ضبط
                </button>)}

                {isRecording && (<>
                    <button
                        onClick={togglePauseResume}
                        className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                        {isPaused ? (<>
                            <Play className="w-5 h-5"/>
                            ادامه
                        </>) : (<>
                            <Pause className="w-5 h-5"/>
                            توقف موقت
                        </>)}
                    </button>

                    <button
                        onClick={handleStopRecording}
                        className="flex items-center gap-2 px-6 py-4 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                        <Square className="w-5 h-5"/>
                        پایان ضبط
                    </button>
                </>)}
                {!isRecording && hasRecording && (<>
                    <button
                        onClick={handleStartRecording}
                        className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    >
                        <Mic className="w-5 h-5"/>
                        ضبط جدید
                    </button>
                </>)}


            </div>
        </div>

        {hasRecording && getBlob() && (<div className="mt-6 p-4 bg-slate-50 rounded-2xl">
            <p className="text-sm   mb-3 font-semibold">پیش‌نمایش صدا:</p>
            <audio
                controls
                src={URL.createObjectURL(getBlob())}
                className="w-full rounded-lg"
            />
        </div>)}
        <div className="flex flex-wrap gap-3 justify-center w-full">
            {!isRecording && hasRecording && (<>
                <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                    <Download className="w-5 h-5"/>

                </button>

                <button
                    onClick={handleSend}
                    className=" flex-1 flex items-center text-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                    <Send className="w-5 h-5"/>
                    ارسال
                </button>

                <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                    <Trash2 className="w-5 h-5"/>

                </button>


            </>)}
        </div>


    </div>);
}
